<script setup lang="ts">
import { computed, h, type VNode, type VNodeChild } from 'vue';
import {
  NEllipsis,
  useDialog,
  type DataTableColumns,
  type DataTableSortState,
  type PaginationProps,
} from 'naive-ui';
import {
  ContractManagementColumnExtractionScope,
  type ContractTypeRef,
  type ExtractionPromptInDb,
  type ExtractionPromptSearchEntry,
} from '@/api';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaEditButton from '@/components/design/base/GaiaEditButton.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip, { gaiaTooltipProps } from '@/components/design/base/GaiaTooltip.vue';
import { gaiaFontSize, gaiaTextTone } from '@/components/design/themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  ExtractionPromptsService,
  type PromptSort,
} from '@/services/contractManagement/extractionPromptsService';
import { responseTypeLabel } from '@/utils/contractManagementView/columnQuestion';
import { formatDateTimeForTable } from '@/utils/datetimeUtils';

/**
 * A page of the organisation's extraction prompts, server-paged by the caller.
 * Org admins edit a prompt from its row and delete it from the row menu.
 */
const props = withDefaults(
  defineProps<{
    prompts: ExtractionPromptSearchEntry[];
    loading: boolean;
    pagination: PaginationProps;
    canWrite: boolean;
    /**
     * Set on a contract type's page: its prompts are listed, so the contract types
     * column goes and the menu offers removing a prompt from that type.
     */
    contractTypeUid?: string;
    /**
     * The server-side sort, when the page sorts: Name, Type and Created at get sort
     * arrows, and a click emits `sort`. Null while the default order applies.
     */
    sort?: PromptSort | null;
    sortable?: boolean;
    /**
     * Clip each prompt to one line, the full text on hover: for when a side panel narrows the
     * table and the wrapped prompt would run to dozens of lines. The table lays out fixed then,
     * so the prompt column takes only the width the others leave instead of growing to fit, and
     * Name and Created at are left out to give it that width.
     */
    truncatePrompt?: boolean;
  }>(),
  { contractTypeUid: undefined, sort: null, sortable: false, truncatePrompt: false }
);

const emit = defineEmits<{
  /** A prompt was saved, removed from the type or deleted: re-fetch the rows. */
  changed: [];
  /** Edit a prompt (its row or pencil): the page opens it in its prompt panel. */
  edit: [prompt: ExtractionPromptSearchEntry];
  /** A sortable header was clicked; null goes back to the default order. */
  sort: [sort: PromptSort | null];
}>();

/** Each sortable column's key is the prompt field the backend sorts by. */
function sortProps(key: string): { sorter?: true; sortOrder?: 'ascend' | 'descend' | false } {
  if (!props.sortable) return {};
  if (props.sort?.key !== key) return { sorter: true, sortOrder: false };
  return { sorter: true, sortOrder: props.sort.direction === 'asc' ? 'ascend' : 'descend' };
}

function handleSorter(sorter: DataTableSortState | DataTableSortState[] | null): void {
  if (Array.isArray(sorter)) return;
  emit(
    'sort',
    sorter?.order
      ? { key: String(sorter.columnKey), direction: sorter.order === 'ascend' ? 'asc' : 'desc' }
      : null
  );
}

const dialog = useDialog();
const message = useGaiaToast();

function openEdit(prompt: ExtractionPromptSearchEntry): void {
  emit('edit', prompt);
}

async function removeFromType(prompt: ExtractionPromptInDb, typeUid: string): Promise<void> {
  try {
    await ExtractionPromptsService.update(
      prompt.uid,
      {
        contract_type_uids: (prompt.contract_types ?? [])
          .map(type => type.uid)
          .filter(uid => uid !== typeUid),
      },
      // A contract type edit re-extracts nothing.
      ContractManagementColumnExtractionScope.NEW_ONLY
    );
    message.success('Removed from this contract type');
    emit('changed');
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to remove the prompt');
  }
}

function confirmDelete(prompt: ExtractionPromptInDb): void {
  dialog.warning({
    title: `Delete ${prompt.display_name}?`,
    content:
      'It is deleted from every contract type, and views can no longer pick it. A prompt a view column asks can’t be deleted.',
    positiveText: 'Delete prompt',
    negativeText: 'Cancel',
    positiveButtonProps: { type: 'error' },
    onPositiveClick: async () => {
      try {
        await ExtractionPromptsService.remove(prompt.uid);
        message.success('Prompt deleted');
        emit('changed');
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'Failed to delete the prompt');
      }
    },
  });
}

function renderActions(prompt: ExtractionPromptSearchEntry): VNode {
  // Their own clicks must not also open the row.
  return h(
    'div',
    {
      style: { display: 'flex', justifyContent: 'flex-end', gap: '2px' },
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
      },
    },
    [
      h(GaiaEditButton, {
        size: 'md',
        subject: 'prompt',
        tooltip: true,
        onClick: () => {
          openEdit(prompt);
        },
      }),
      // Only on a contract type's page: unlinks the prompt from that type, and keeps it.
      props.contractTypeUid
        ? h(GaiaDeleteButton, {
            size: 'md',
            intent: 'remove',
            title: 'Remove from this contract type',
            tooltip: true,
            onClick: () => {
              if (props.contractTypeUid) void removeFromType(prompt, props.contractTypeUid);
            },
          })
        : null,
      h(GaiaDeleteButton, {
        size: 'md',
        subject: 'prompt',
        tooltip: true,
        onClick: () => {
          confirmDelete(prompt);
        },
      }),
    ]
  );
}

/** The first two types as tags; the rest as "+N", named in its tooltip. */
function renderContractTypes(types: ContractTypeRef[]): VNodeChild {
  if (types.length === 0) return h(GaiaText, { size: 'dense', tone: 'faint' }, () => '—');
  const shown = types.slice(0, 2);
  const rest = types.slice(2);
  return h(
    'div',
    { style: { display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' } },
    [
      ...shown.map(type => h(GaiaTag, { variant: 'muted', size: 'tiny' }, () => type.name)),
      rest.length
        ? h(
            // GaiaTooltip wraps long content at its max width instead of stretching across the page.
            GaiaTooltip,
            { placement: 'top' },
            {
              trigger: () =>
                h(GaiaTag, { variant: 'muted', size: 'tiny' }, () => `+${rest.length}`),
              default: () => rest.map(type => type.name).join(', '),
            }
          )
        : null,
    ]
  );
}

const columns = computed((): DataTableColumns<ExtractionPromptSearchEntry> => [
  {
    title: 'Display name',
    key: 'display_name',
    // Narrower beside a side panel, so the clipped prompt keeps some room.
    width: props.truncatePrompt ? 200 : 280,
    ...sortProps('display_name'),
    render: row =>
      h(GaiaText, { size: 'body', weight: 'semibold', tone: 'ink' }, () => row.display_name),
  },
  // The internal name, which default prompts share; left out beside a side panel.
  ...(props.truncatePrompt
    ? []
    : [
        {
          title: 'Name',
          key: 'name',
          width: 220,
          ...sortProps('name'),
          render: (row: ExtractionPromptSearchEntry) =>
            h(GaiaText, { size: 'dense', tone: 'subtle' }, () => row.name),
        },
      ]),
  {
    title: 'Prompt',
    key: 'prompt',
    // Wrapped in full: the prompt is what tells two prompts apart, and the column has the room.
    render: row =>
      props.truncatePrompt
        ? // Styled on the ellipsis, not an inner GaiaText, so the tone stays out of the tooltip.
          h(
            NEllipsis,
            {
              tooltip: gaiaTooltipProps,
              style: {
                maxWidth: '100%',
                fontSize: gaiaFontSize.dense,
                color: gaiaTextTone.tertiary,
              },
            },
            { default: () => row.prompt }
          )
        : h(
            GaiaText,
            {
              size: 'dense',
              tone: 'tertiary',
              style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
            },
            () => row.prompt
          ),
  },
  ...(props.contractTypeUid
    ? []
    : [
        {
          title: 'Contract types',
          key: 'contract_types',
          width: 220,
          render: (row: ExtractionPromptSearchEntry) =>
            renderContractTypes(row.contract_types ?? []),
        },
      ]),
  {
    title: 'Type',
    key: 'response_type',
    width: 120,
    ...sortProps('response_type'),
    render: row => h(GaiaTag, { variant: 'muted' }, () => responseTypeLabel(row.response_type)),
  },
  // Left out beside a side panel: its width goes to the clipped prompt.
  ...(props.truncatePrompt
    ? []
    : [
        {
          title: 'Created at',
          key: 'created_at',
          width: 180,
          ...sortProps('created_at'),
          render: (row: ExtractionPromptSearchEntry) =>
            h(GaiaText, { size: 'dense', tone: 'subtle' }, () =>
              formatDateTimeForTable(row.created_at)
            ),
        },
      ]),
  ...(props.canWrite
    ? [
        {
          title: '',
          key: 'actions',
          width: props.contractTypeUid ? 110 : 80,
          align: 'right' as const,
          render: renderActions,
        },
      ]
    : []),
]);

/** Admins open a prompt's row to edit it. */
function rowProps(row: ExtractionPromptSearchEntry): Record<string, unknown> {
  if (!props.canWrite) return {};
  return {
    style: { cursor: 'pointer' },
    onClick: (): void => {
      openEdit(row);
    },
  };
}
</script>

<template>
  <gaia-table
    :columns="columns"
    :data="prompts"
    :loading="loading"
    remote
    :pagination="pagination"
    :row-key="(row: ExtractionPromptSearchEntry) => row.uid"
    :row-props="rowProps"
    :table-layout="truncatePrompt ? 'fixed' : 'auto'"
    @update:sorter="handleSorter"
  >
    <template v-if="$slots.empty" #empty>
      <slot name="empty" />
    </template>
  </gaia-table>
</template>
